import React, { useState } from "react";
import ModalBase from "../../../components/modals/ModalBase";
import { TClass } from "../../../types/Types";
import { useFormik } from "formik";
import { Urls } from "../../../utils/urls";
import apiRequest from "../../../helpers/apiRequest";
import TextInputComp from "../../../components/inputs/TextInputComp";
import CustomDropDown from "../../../components/dropDown/CustDropDown";
import Button from "../../../components/buttons/Button";
import * as Yup from 'yup';
import { ClipboardDocumentIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";

type GLModal = {
    onClose: () => void;
    isOpen: boolean;
    classItem: TClass;
};

const roleOptions = [
    { value: "host", label: "Host" },
    { value: "user", label: "User" }
];

const validationSchema = Yup.object({
    name: Yup.string()
        .required('Name is required')
});

const GenereteLinkModal = ({ isOpen, onClose, classItem }: GLModal) => {
    const [link, setLink] = useState('');
    const [copied, setCopied] = useState(false);

    const setupLink = (jwt: string, name: string) => {
        const domain = `${window.location.protocol}//${window.location.host}`;
        const sessionName = classItem.name;
        const sessionKey = classItem.sessionKey ?? '';
        setLink(`${domain}/meeting?sessionName=${sessionName}&name=${name}&sessionKey=${sessionKey}&jwt=${jwt}`);
    };

    const onSubmit = async (
        values: any,
        { setSubmitting, resetForm }: { setSubmitting: any; resetForm: any }
    ): Promise<void> => {
        const { data, error } = await apiRequest<{ jwt: string }>({
            method: "GET",
            url: `${Urls.get_meeting_jwt}/${classItem._id}?role=${values.role}`,
        });

        if (error) {
            alert(error);
        } else {
            if (data?.jwt) {
                setupLink(data?.jwt, values.name);
            }
        }
        setSubmitting(false);
    };

    const copyHandler = () => {
        navigator.clipboard.writeText(link ?? '')
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => {
                console.error('Failed to copy: ', err);
            });
    };

    const {
        values,
        handleSubmit,
        handleChange,
        setFieldValue,
        isSubmitting,
        errors,
        submitForm
    } = useFormik({
        initialValues: {
            name: "",
            role: 'host'
        },
        validationSchema,
        onSubmit: (values, { setSubmitting, resetForm }) =>
            onSubmit(values, { setSubmitting, resetForm })
    });

    return (
        <ModalBase
            isOpen={isOpen}
            onClose={onClose}
            title={link ? "Class Link" : "Generate Link"}
            primaryBtn={!link ? { 
                title: "Generate", 
                onClick: () => submitForm(),
                loading: isSubmitting 
            } : undefined}
            secondaryBtn={{ 
                title: link ? "Done" : "Cancel", 
                onClick: onClose 
            }}
        >
            <div className="py-2">
                {link ? (
                    <div className="flex flex-col gap-4">
                        <div className="p-3 bg-green-50 text-green-700 text-sm rounded border border-green-100">
                            The meeting link has been successfully generated.
                        </div>
                        
                        <div className="w-full bg-gray-50 rounded p-4 border border-gray-200 relative">
                            <p className="text-[10px] text-gray-400 uppercase font-bold mb-2">Meeting URL</p>
                            <p className="text-sm text-gray-600 break-all pr-12 font-mono">
                                {link}
                            </p>
                            <button
                                onClick={copyHandler}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded bg-white border border-gray-200 text-gray-500 hover:text-primary transition-all"
                                title="Copy"
                            >
                                {copied ? (
                                    <ClipboardDocumentCheckIcon className="w-5 h-5 text-primary" />
                                ) : (
                                    <ClipboardDocumentIcon className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                        
                        {copied && (
                            <p className="text-primary text-xs font-semibold text-center mt-1">
                                Copied to clipboard
                            </p>
                        )}
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <div className="text-sm font-medium text-gray-700">
                            Generating link for: <span className="text-primary">{classItem.name}</span>
                        </div>

                        <TextInputComp
                            id="name"
                            name="name"
                            required
                            value={values.name}
                            onChange={handleChange}
                            label="Name"
                            placeholder="Full Name"
                            error={errors.name}
                        />

                        <CustomDropDown
                            placeholder="Select role"
                            label={"Role"}
                            value={roleOptions?.find((a) => a.value === values.role)?.label}
                            options={roleOptions}
                            optionLabelKey="label"
                            onChange={(option) => setFieldValue("role", option.value)}
                        />
                    </form>
                )}
            </div>
        </ModalBase>
    );
};

export default GenereteLinkModal;
